import { setOrganizationFullAddress } from 'src/utils/escrow';
import { type IBeneficiary } from 'src/graphql/schema';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { Card, CardTitle, CardHeader, CardContent, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

import { BeneficiaryPicker, CreateBeneficiaryForm, EditBeneficiaryForm } from '.';

import { useBeneficiaryContext } from '../model';

const descriptions = {
  select:
    "Select the organization that will be named as beneficiary in the escrow agreement. If it isn't listed, add it and enter its registered name and address.",
  form: "Enter the beneficiary's organization details. These will be listed in the escrow agreement.",
};

const CardWrapper = ({
  children,
  description,
  footer,
}: {
  children: React.ReactNode;
  description?: string;
  footer?: React.ReactNode;
}) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Beneficiary organization details</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      {children}
      {footer}
    </Card>
  );
};

const BeneficiaryDetails = ({ beneficiary }: { beneficiary: IBeneficiary }) => {
  const { canChangeBeneficiary, setMode } = useBeneficiaryContext();

  const startEditing = () => setMode('edit');
  const startChanging = () => setMode('select');

  const address = {
    street: beneficiary.street || '',
    streetNumber: beneficiary.streetNumber || '',
    city: beneficiary.city || '',
    postalCode: beneficiary.postalCode || '',
    region: beneficiary.region || '',
    country: beneficiary.country || '',
  };

  const rows = [
    {
      id: 'organization-name',
      label: 'Organization Name',
      value: beneficiary.companyName,
    },
    {
      id: 'website',
      label: 'Website',
      value: beneficiary.companyWebsite || '-',
    },
    {
      id: 'registration-number',
      label: 'Registration Number',
      value: beneficiary.companyRegistrationNumber || '-',
    },
    {
      id: 'address',
      label: 'Address',
      value: setOrganizationFullAddress(address),
    },
  ];

  return (
    <CardWrapper
      footer={
        beneficiary.policy.update && (
          <CardFooter className="bg-muted py-3 justify-end gap-2 rounded-b-xl">
            {canChangeBeneficiary && (
              <Button variant="secondary" size="sm" onClick={startChanging}>
                Change beneficiary
              </Button>
            )}
            <Button variant="secondary" size="sm" onClick={startEditing}>
              Edit
            </Button>
          </CardFooter>
        )
      }
    >
      <CardContent>
        <Table className="border-y border-border">
          <TableBody>
            {rows.map(({ id, label, value }) => (
              <TableRow key={id}>
                <TableCell className="pl-0 py-3 w-50 font-semibold text-slate-700">{label}</TableCell>
                <TableCell className="py-3 px-0 w-4">:</TableCell>
                <TableCell className="py-3 text-foreground">
                  <div className="min-w-47.5">{value}</div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </CardWrapper>
  );
};

const BeneficiaryOrganization = () => {
  const { beneficiary, mode } = useBeneficiaryContext();

  if (mode === 'create')
    return (
      <CardWrapper description={descriptions.form}>
        <CreateBeneficiaryForm />
      </CardWrapper>
    );

  if (!beneficiary || mode === 'select')
    return (
      <CardWrapper description={descriptions.select}>
        <CardContent>
          <BeneficiaryPicker />
        </CardContent>
      </CardWrapper>
    );

  if (mode === 'edit')
    return (
      <CardWrapper description={descriptions.form}>
        <EditBeneficiaryForm />
      </CardWrapper>
    );

  return <BeneficiaryDetails beneficiary={beneficiary} />;
};

export default BeneficiaryOrganization;
